/* ============================================================================
   ffg-freeship.js — keeps the free-shipping tick badge honest after the cart
   changes without a page render.

   IT DOES NOT DRAW THE FIRST STATE. blocks/custom-product-copy.liquid renders
   the badge already correct, because Liquid can see cart.total_price on every
   request — including the Section Rendering API re-render Horizon fires on
   each variant change. So there is no wrong-state flash on load, a re-rendered
   product form arrives correct with no re-initialisation, and if this file
   fails to load the badge is still right until the shopper adds something.

   The one case Liquid cannot see is the cart mutating in place: add to cart,
   the drawer's +/- keys, a line removed. That is all this file covers.

   HOW IT KNOWS. There is no cart event this theme can be relied on to fire —
   assets/custom.header-desktop.js already learned that and settled on the same
   two-pronged approach used here: listen for every cart event name in
   circulation, AND watch the cart bubble's own text, which changes no matter
   which code path did the changing. Either signal triggers one /cart.js read.

   WHAT IT WRITES. A class, an aria-label and the tip's text — nothing else.
   The threshold, all four message templates and the store's money format come
   down as data attributes on the badge, so no copy and no currency assumption
   lives in this file.
   ========================================================================= */
(function () {
  'use strict';

  /* The block emits this tag inside the section, so a re-render re-inserts it
     and the browser runs it again. Idempotent by flag, same as
     ffg-buybar-total.js — the block is where the guard condition lives. */
  if (window.__ffgFreeShip) return;
  window.__ffgFreeShip = true;

  var SEL = '[data-ffg-freeship]';
  /* The last total we actually read. A re-rendered badge is painted from this
     rather than from another network round trip — Liquid already gave it the
     right state, but only as of the request, and the cart may have moved since
     within the same page view. */
  var lastTotal = null;

  function money(cents, sample) {
    var prefix = '$', suffix = '', dec = '.', group = ',', showCents = true;
    var m = sample && sample.match(/^(\D*?)([\d][\d.,\s ']*)(\D*)$/);
    if (m) {
      prefix = m[1];
      suffix = m[3];
      var body = m[2];
      var tail = body.match(/[.,](\d{2})$/);
      showCents = !!tail;
      if (tail) dec = body.charAt(body.length - 3);
      var head = tail ? body.slice(0, -3) : body;
      var g = head.match(/[^\d]/);
      if (g) group = g[0];
    }
    cents = Math.abs(cents);
    var whole = Math.floor(cents / 100);
    var frac = cents % 100;
    var out = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, group);
    if (showCents) out += dec + (frac < 10 ? '0' + frac : frac);
    return prefix + out + suffix;
  }

  function paint(total) {
    if (total === null || !isFinite(total)) return;
    lastTotal = total;

    var badges = document.querySelectorAll(SEL);
    for (var i = 0; i < badges.length; i++) {
      var el = badges[i];
      var min = parseInt(el.getAttribute('data-threshold'), 10);
      /* A missing or unparseable threshold means we have no idea what state
         this badge should be in, so it is left exactly as Liquid rendered it.
         Guessing here would be the one way this script shows something wrong
         rather than something stale. */
      if (!isFinite(min) || min <= 0) continue;

      var short = min - total;
      if (short < 0) short = 0;
      var met = short === 0;
      var amount = met ? '' : money(short, el.getAttribute('data-money-sample'));

      el.classList.toggle('is-met', met);

      var tipText = met
        ? el.getAttribute('data-tip-met')
        : (el.getAttribute('data-tip-short') || '').replace('[amount]', amount);
      var label = met
        ? el.getAttribute('data-label-met')
        : (el.getAttribute('data-label-short') || '').replace('[amount]', amount);

      /* WRITE ONLY WHAT CHANGED, and never unconditionally.

         `tip.textContent = x` replaces the text node EVERY TIME, even when x
         is the string already there — that is what the spec's "replace all"
         does — so an unconditional write is a childList mutation on every
         call. The tip lives inside the subtree watchForm() observes, so each
         write re-entered the observer, which repainted, which wrote again:
         an unbounded microtask chain that starves the event loop. Deleting a
         cart line froze the tab (2 Aug, found by Devin: "it really bogs down
         Firefox").

         Comparing first is the fix and not merely an optimisation. It is the
         only reason the observer below is safe to keep. */
      var tip = el.querySelector('[data-ffg-freeship-tip]');
      if (tip && tipText && tip.textContent !== tipText) tip.textContent = tipText;
      if (label && el.getAttribute('aria-label') !== label) el.setAttribute('aria-label', label);
    }
  }

  /* ---- SHARED CART READ -------------------------------------------------
     Two scripts watch the cart and both want the same answer: custom.header-
     desktop.js for the header count, ffg-freeship.js for the shipping badge.
     Each used to fetch /cart.js on its own, so every add, delete or quantity
     change cost two identical round trips.

     DEFINED DEFENSIVELY IN BOTH FILES, IDENTICALLY, GUARDED ON window. That is
     deliberate and it is what avoids the obvious drawback of sharing: neither
     script may depend on the other having loaded. This one runs on every page
     with a masthead; the badge only exists on product pages. Whichever arrives
     first defines the reader, the second sees it and reuses it, and either can
     be removed tomorrow without breaking the other.

     TWO LAYERS OF DEDUPE, because they solve different problems:
       - inflight: concurrent callers share one request. This is the common
         case — one cart change fires several events at once.
       - a 300ms cache: callers that arrive just AFTER a request settles reuse
         the answer instead of asking again. Short on purpose; the cart is
         authoritative elsewhere and a stale count is worse than a fetch.

     It never throws. A failed read resolves null and each caller leaves its own
     display alone, which is the same behaviour they had independently. */
  window.__ffgCartRead = window.__ffgCartRead || (function () {
    var inflight = null;
    var last = null;
    var lastAt = 0;
    return function (maxAgeMs) {
      var age = typeof maxAgeMs === 'number' ? maxAgeMs : 300;
      if (last && Date.now() - lastAt < age) return Promise.resolve(last);
      if (inflight) return inflight;
      if (!window.fetch) return Promise.resolve(null);
      inflight = fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) {
          inflight = null;
          if (c) { last = c; lastAt = Date.now(); }
          return c;
        })
        .catch(function () { inflight = null; return null; });
      return inflight;
    };
  })();

  /* The `pending` flag this used to carry is gone — the shared reader dedupes
     concurrent callers itself, and doing it in both places meant a failed read
     could leave this one latched shut. */
  function refresh() {
    window.__ffgCartRead().then(function (c) { if (c) paint(c.total_price); });
  }

  ['cart:updated', 'cart:refresh', 'cart:change', 'cart:build', 'cart:add',
   'on:cart:add', 'variant:add', 'ajaxProduct:added', 'product:added']
    .forEach(function (ev) { document.addEventListener(ev, refresh); });

  /* THE BUBBLE IS THE RELIABLE SIGNAL. Whatever path changed the cart, the
     count in the header changes with it, so its text is a signal no code path
     can skip. Watched rather than polled, and it only ever calls refresh() —
     this observer never writes into the tree it watches, so it cannot loop. */
  function watchBubble() {
    var bubble = document.querySelector(
      'cart-icon [data-cart-count], .cart-count, .cart-bubble__count, [ref=cartBubbleCount]'
    );
    if (!bubble || !window.MutationObserver || bubble.dataset.ffgShipObserved) return;
    bubble.dataset.ffgShipObserved = '1';
    new MutationObserver(refresh).observe(bubble, {
      childList: true, characterData: true, subtree: true
    });
  }

  /* A variant tap replaces the product form wholesale, so the badge in the DOM
     after one is a fresh element carrying the state as of the RENDER. If the
     cart has moved since this page loaded, repaint it from what we already
     know — no request, and nothing to do on the common path where the total
     never changed. */
  function watchForm() {
    var scope = document.querySelector('[id^="ProductInformation-"]');
    var host = (scope && scope.closest('[id^="shopify-section-"]'));
    if (!host || !window.MutationObserver) return;
    /* THIS OBSERVER WATCHES A TREE THE CALLBACK WRITES INTO — the badge is
       inside the product section. That is legal only because paint() compares
       before it writes, so a repaint that changes nothing produces no mutation
       and the chain stops after one pass. The re-entrancy flag is the second
       belt: it bounds the depth at one even if a future edit reintroduces an
       unconditional write, turning a frozen tab back into a stale one.

       If you ever add a write to paint(), make it conditional. */
    var painting = false;
    new MutationObserver(function () {
      if (lastTotal === null || painting) return;
      painting = true;
      try { paint(lastTotal); } finally { painting = false; }
    }).observe(host, { childList: true, subtree: true });
  }

  function init() {
    watchBubble();
    watchForm();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
